import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

function StatusBadge({ status }) {
  const cls =
    status === "Completed"
      ? "status-badge status-completed"
      : status === "Failed"
      ? "status-badge status-failed"
      : status === "In Progress"
      ? "status-badge status-in-progress"
      : "status-badge status-pending";
  return <span className={cls}>{status || "Pending"}</span>;
}

function DetailModal({ request, onClose, onRetryDone, instance }) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState("");
  const [liveLog, setLiveLog] = useState(request.actionLog || "");
  const [liveStatus, setLiveStatus] = useState(request.status);

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const handleRetry = async () => {
    setRetrying(true);
    setRetryError("");
    try {
      const token = await getApiToken(instance);
      const res = await axios.post(
        `${API_BASE_URL}/offboarding/execute`,
        {
          itemId: request._itemId,
          transactionNumber: request.transactionNumber,
          userPrincipalName: request.userPrincipalName,
          delegates: request.delegates,
          submittedBy: request.submittedBy,
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setLiveLog(res.data.actionLog || "");
      setLiveStatus(res.data.status);
      onRetryDone();
    } catch (err) {
      setRetryError(err.response?.data?.error || "Retry failed. Please try again.");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="txn-code">{request.transactionNumber}</span>
            <StatusBadge status={liveStatus} />
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <div className="modal-details">
            <div className="detail-row">
              <span className="detail-label">Employee</span>
              <span className="detail-value">{request.employeeName || "—"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">UPN</span>
              <span className="detail-value">{request.userPrincipalName || "—"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Submitted By</span>
              <span className="detail-value">{request.submittedBy || "—"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Day</span>
              <span className="detail-value">{formatDate(request.lastDay)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Delegates</span>
              <span className="detail-value">
                {request.delegates
                  ? request.delegates.split(",").map((d) => d.trim()).filter(Boolean).join(", ")
                  : "None"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date Submitted</span>
              <span className="detail-value">{formatDate(request.dateSubmitted)}</span>
            </div>
            {request.completedOn && (
              <div className="detail-row">
                <span className="detail-label">Completed On</span>
                <span className="detail-value">{formatDate(request.completedOn)}</span>
              </div>
            )}
          </div>

          {request.removedGroups && (
            <div className="removed-groups">
              <div className="action-log-label">Removed Groups (Rollback Reference)</div>
              <ul className="removed-groups-list">
                {request.removedGroups
                  .split("\n")
                  .filter(Boolean)
                  .map((line, i) => {
                    const match = line.match(/^(.*?)\s*\[([^\]]+)\]$/);
                    const name = match ? match[1].trim() : line;
                    const id   = match ? match[2] : null;
                    return (
                      <li key={i} className="removed-group-item">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="group-icon" aria-hidden="true">
                          <rect x="1" y="1" width="12" height="12" rx="3" stroke="#6b7280" strokeWidth="1.5" fill="none"/>
                          <path d="M4 7h6M7 4v6" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span className="group-name">{name}</span>
                        {id && <code className="group-id">{id}</code>}
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {liveLog && (
            <div className="action-log">
              <div className="action-log-label">Action Log</div>
              <pre className="action-log-content">{liveLog}</pre>
            </div>
          )}

          {retryError && <p className="field-error" style={{ marginTop: 8 }}>{retryError}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          {(liveStatus === "Failed" || liveStatus === "Pending") && (
            <button
              className="btn-primary"
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? <><span className="btn-spinner" /> Retrying...</> : "Retry Automation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HistoryView({ onNewRequest }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken(instance);
      const res = await axios.get(`${API_BASE_URL}/offboarding/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load offboarding requests.");
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = requests.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.transactionNumber?.toLowerCase().includes(q) ||
      r.employeeName?.toLowerCase().includes(q) ||
      r.userPrincipalName?.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const handleRetryDone = () => {
    fetchRequests();
  };

  return (
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Offboarding</span>
          </div>
          <div className="header-actions">
            <button className="btn-primary btn-header" onClick={onNewRequest}>
              + Submit Request
            </button>
            <span className="header-username">{user.name || user.username}</span>
          </div>
        </div>
      </header>

      <main className="history-container">
        <div className="history-title-row">
          <div>
            <h2 className="history-title">Offboarding Requests</h2>
            <p className="history-subtitle">{requests.length} total record{requests.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="history-toolbar">
            <input
              type="text"
              className="history-search"
              placeholder="Search by name, UPN, or transaction number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn-secondary" onClick={fetchRequests}>
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="history-error">{error}</div>}

        {loading && (
          <div className="history-loading">
            <span className="search-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
            <p>Loading offboarding requests...</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="history-empty">
            <p>{search ? "No records match your search." : "No offboarding requests found."}</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="history-card">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Transaction Number</th>
                  <th>Employee Name</th>
                  <th>User Principal Name</th>
                  <th>Status</th>
                  <th>Date Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={i}
                    className="history-row-clickable"
                    onClick={() => setSelectedRequest(r)}
                  >
                    <td>
                      <span className="txn-code">{r.transactionNumber || "—"}</span>
                    </td>
                    <td className="td-name">{r.employeeName || "—"}</td>
                    <td className="td-upn">{r.userPrincipalName || "—"}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="td-date">{formatDate(r.dateSubmitted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedRequest && (
        <DetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onRetryDone={handleRetryDone}
          instance={instance}
        />
      )}
    </div>
  );
}
